import { Injectable } from '@angular/core';
import { AccountRepositoryService } from './account-repository.service';
import { SurveyRepositoryService } from './survey-repository.service';
import { Router } from '@angular/router';
import axios, { AxiosError } from 'axios';
import { Filesystem, Directory } from '@capacitor/filesystem';
import _ from 'lodash';
import { AnswersRepositoryService } from './answers-repository.service';
import { Network } from '@capacitor/network';
import { environment } from '../environments/environment';
import { Device } from '@capacitor/device';
import { async, BehaviorSubject, Subject } from 'rxjs';
import { Geolocation } from '@capacitor/geolocation';
import { App } from '@capacitor/app';
import { HistoryNavigationRepositoryService } from './history-navigation-repository.service';
import { AppComponent } from './app.component';
import { NoSurveyFoundPage } from './no-survey-found/no-survey-found.page';

@Injectable({
  providedIn: 'root',
})
export class ExpressyonService {
  isConnected: boolean = true;
  isConnectedChange: Subject<boolean> = new Subject<boolean>();

  networkStatus: boolean = false;
  networkStatusChange: Subject<boolean> = new Subject<boolean>();

  checkUpdateSurveyTimer;
  //Obtient ou définit un boolén indiquant que la version de l'application installée n'est pas
  isUpdateAppAvailable = false;
  isPreview: boolean = false;
  isReturningFromBannerButton: boolean = false;

  imageAnswer: {} = {};

  constructor(
    private accountRepository: AccountRepositoryService,
    private surveyRepository: SurveyRepositoryService,
    private router: Router,
    private answersRepository: AnswersRepositoryService,
    private historyNavigationRepository: HistoryNavigationRepositoryService
  ) {
    this.isConnectedChange.subscribe((value) => {
      this.isConnected = value;
    });

    this.networkStatusChange.subscribe((value) => {
      this.networkStatus = value;
    });
  }

  device = {};
  setDevice = async () => {
    try {
      const deviceInfos = await Device.getInfo();
      const deviceId = await Device.getId();
      this.device = {
        cordova: 'Capacitor : 3.5.1', //Changement de plugin à partir de la 3.0.0 -> capacitor
        model: deviceInfos.model,
        platform: deviceInfos.platform,
        // Capacitor 5+ : DeviceId.uuid a été renommé en identifier.
        // On garde la clé "uuid" côté device pour ne pas casser l'API backend.
        uuid: deviceId.identifier,
        version: deviceInfos.osVersion,
      };
    } catch (error) {
      console.error('setDevice a échoué, uuid indisponible', error);
    }
  };

  // S'assure que this.device est rempli avant de construire un payload qui
  // en dépend : setDevice() est lancé sans await au démarrage et peut avoir
  // échoué silencieusement. Un uuid '0' (valeur par défaut envoyée au
  // backend) est considéré comme manquant.
  private async ensureDevice() {
    const uuid = (this.device as any).uuid;
    if (this.getIsWeb() || (uuid && uuid !== '0')) return;
    await this.setDevice();
  }

  getNetworkStatus() {
    return this.networkStatus;
  }

  setNetworkStatus(value) {
    if (this.getNetworkStatus() == value) return false;
    if (value) this.sendInfos();

    this.networkStatusChange.next(value);
    return true;
  }

  setConnected(value) {
    if (this.getConnected() == value) return;
    this.isConnectedChange.next(value);
    if (value) this.setNetworkStatus(true);
  }

  getConnected = function () {
    return this.isConnected;
  };

  setPreview(value) {
    this.isPreview = value;
  }

  getPreview() {
    return this.isPreview;
  }

  setReturningFromBannerButton(value: boolean) {
    this.isReturningFromBannerButton = value;
  }

  getReturningFromBannerButton() {
    return this.isReturningFromBannerButton;
  }

  hasSurvey() {
    if (!this.surveyRepository.getSurvey()) {
      return false;
    }
    return this.surveyRepository.getSurvey().id ? true : false;
  }

  isWeb = false;
  setIsWeb(value) {
    this.isWeb = value;
    if (value) {
      this.setNetworkStatus(true);
    }
  }
  getIsWeb() {
    return this.isWeb;
  }

  getIsUpdateAppAvailable() {
    return this.isUpdateAppAvailable;
  }

  //Définit un boolén indiquant que l'application est à jour
  setUpdateAppAvailable(serverAppVersion) {
    var splittedServerAppVersion = serverAppVersion.replace('.', '');
    var splittedAppVersion = this.accountRepository
      .getAppVersion()
      .replace('.', '');
    if (parseInt(splittedServerAppVersion) !== parseInt(splittedAppVersion)) {
      return true;
    }
    return false;
  }

  //Retourne le timer responsable du systeme de pull des mises à jour du formulaire
  getCheckUpdateSurveyTimer = function () {
    return this.checkUpdateSurveyTimer;
  };

  hasDefaultPassword = function () {
    return this.accountRepository.getPassword().toUpperCase() == '1111';
  };

  validatePassword = function (password) {
    return (
      this.accountRepository.getPassword().toUpperCase() ==
        password.toString().toUpperCase() ||
      this.accountRepository.getSuperPassword().toUpperCase() ==
        password.toString().toUpperCase()
    );
  };

  async login(key, name) {
    const status = await this.getNetworkStatus();
    const self = this;
    if (!status) {
      throw 'ERROR.NO_CONNECTION';
    }
    if (!this.accountRepository.isLogged()) {
      await this.ensureDevice();
      let config = {
        name: name,
        clientKey: key,
        appVersion: environment.APP_VERSION,
        // _.defaults avec {} en cible : ne pas muter this.device avec les
        // valeurs par défaut, sinon l'uuid '0' resterait collé définitivement.
        mobile: _.defaults({}, this.device, {
          cordova: '6.3.1', //Changement de plugin à partir de la 3.0.0 -> capacitor
          model: 'Nexus One',
          platform: 'Android',
          uuid: '0',
          version: self.accountRepository.getAppVersion(),
        }),
      };
      var data = await axios
        .post(`${environment.API_HOST}/api/accounts`, config)
        .then(function (res) {
          const data = res.data;
          self.accountRepository.setApiKey(data.apiToken);
          self.accountRepository.setClientKey(key);
          self.accountRepository.setAccountName(name);
          self.accountRepository.setTimeStamp(data.timestamp);
          self.accountRepository.setPassword(data.password);
          self.accountRepository.setSuperPassword(data.superPassword);
          self.accountRepository.setIsInAdminMode(data.isInAdminMode);
          self.accountRepository.setSurveyInfos(data.surveysInfos);
          self.accountRepository.setFoldersInfos(data.foldersInfos);
          self.accountRepository.setTabletName(data.tabletName);
          self.accountRepository.saveTabletInfos(
            data.tabletName,
            // name,
            undefined,
            undefined,
            undefined
          );
          self.sendDatas();
          if (!self.getIsWeb()) {
            self.sendDeviceInfos();
          }
          return data;
        })
        .catch(function (error) {
          switch (error.response?.status) {
            case 401:
              throw 'ERROR.ACCOUNT_BLOCKED';
            case 402:
              throw 'ERROR.TABLET_UNKNOW';
            case 403:
              throw 'ERROR.ACCOUNT_MAX_CLIENT_REACHED';
            // 400 = "licence key incorrecte ou inexistante" renvoyé par le
            // backend. Sans ce case, un 400 tombait dans le `default` et
            // affichait à tort "vérifiez votre connexion internet".
            case 400:
            case 404:
              throw 'ERROR.ACCOUNT_NOT_FOUND';
            case 500:
              throw 'ERROR.SERVER_ERROR';
            default:
              self.setConnected(false);
              throw 'ERROR.ACCOUNT_UNKNOWN';
          }
        });
      // Réponse du backend (même forme que GET accounts) pour l'appelant
      return data ?? 1;
    }
    return 1;
  }

  /**
   * Récupère les infos du device standalone de cette tablette (page cachée
   * de création de device, bouton "Rafraîchir" du login). Retourne null si
   * le device n'existe pas encore. `licenceKey` est renseigné si la tablette
   * est déjà associée à une licence (apps du device côté backend).
   */
  async getStandaloneDevice(): Promise<any> {
    const status = await this.getNetworkStatus();
    if (!status) {
      throw 'ERROR.NO_CONNECTION';
    }
    await this.ensureDevice();
    const mobile: any = _.defaults({}, this.device, {
      cordova: '6.3.1',
      model: 'Nexus One',
      platform: 'Android',
      uuid: '0',
      version: this.accountRepository.getAppVersion(),
    });
    return axios
      .get(`${environment.API_HOST}/api/standalone-devices/${mobile.uuid}`)
      .then((res) => res.data)
      .catch((error) => {
        if (error.response?.status === 404) return null;
        throw 'ERROR.SERVER_ERROR';
      });
  }

  /**
   * Crée le device standalone de cette tablette (sans licence), ou met à
   * jour son serialId s'il existe déjà. Le nom est généré aléatoirement
   * par le backend à la création.
   */
  async saveStandaloneDevice(serialId: string): Promise<any> {
    const status = await this.getNetworkStatus();
    if (!status) {
      throw 'ERROR.NO_CONNECTION';
    }
    await this.ensureDevice();
    const config = {
      serialId: serialId,
      mobile: _.defaults({}, this.device, {
        cordova: '6.3.1',
        model: 'Nexus One',
        platform: 'Android',
        uuid: '0',
        version: this.accountRepository.getAppVersion(),
      }),
    };
    return axios
      .post(`${environment.API_HOST}/api/standalone-devices`, config)
      .then((res) => res.data)
      .catch(() => {
        throw 'ERROR.SERVER_ERROR';
      });
  }

  async weblogin(survey_shortId, mode): Promise<any> {
    const self = this;
    let config = {
      survey_shortId: survey_shortId,
      mode: mode,
      appVersion: environment.APP_VERSION,
    };

    return axios
      .post(`${environment.API_HOST}/api/webaccounts?timestamp=0`, config)
      .then(function (res) {
        const data = res.data;
        var apiToken = data.apiToken;
        var key = data.key;
        if (apiToken && data) {
          self.accountRepository.setApiKey(apiToken);
          self.accountRepository.setClientKey(key);
          self.accountRepository.setLicenceType(data.licenceType);
          self.accountRepository.setAccountName(data.accountName);
          self.accountRepository.setTimeStamp(data.timestamp);
          self.accountRepository.setPassword(data.password);
          self.accountRepository.setSuperPassword(data.superPassword);
          self.accountRepository.setIsInAdminMode(data.isInAdminMode);
          self.accountRepository.setSurveyInfos(data.surveysInfos);
          self.accountRepository.setTabletName(data.tabletName);
          return res; // Return the data
        } else {
          throw 'ERROR.ACCOUNT_UNKNOWN';
        }
      })
      .catch(function (error) {
        console.error('Error in weblogin:', error);
        switch (error.response?.status) {
          case 401:
            throw 'ERROR.ACCOUNT_BLOCKED';
          case 402:
            throw 'ERROR.TABLET_UNKNOW';
          case 403:
            throw 'ERROR.ACCOUNT_MAX_CLIENT_REACHED';
          // 400 = "licence key incorrecte ou inexistante" renvoyé par le
          // backend. Sans ce case, un 400 tombait dans le `default` et
          // affichait à tort "vérifiez votre connexion internet".
          case 400:
          case 404:
            throw 'ERROR.ACCOUNT_NOT_FOUND';
          case 500:
            throw 'ERROR.SERVER_ERROR';
          default:
            self.setConnected(false);
            throw 'ERROR.ACCOUNT_UNKNOWN';
        }
      });
  }

  async lastweblogin(key, survey_id): Promise<any> {
    const self = this;
    let config = {
      licenceKey: key,
      survey_id: survey_id,
      appVersion: environment.APP_VERSION,
    };

    return await axios
      .post(`${environment.API_HOST}/api/webaccounts_v2?timestamp=0`, config)
      .then(function (res) {
        const data = res.data;
        var apiToken = data.apiToken;
        if (apiToken && data) {
          self.accountRepository.setApiKey(apiToken);
          self.accountRepository.setClientKey(key);
          self.accountRepository.setAccountName(data.accountName);
          self.accountRepository.setTimeStamp(data.timestamp);
          self.accountRepository.setPassword(data.password);
          self.accountRepository.setSuperPassword(data.superPassword);
          self.accountRepository.setIsInAdminMode(data.isInAdminMode);
          self.accountRepository.setSurveyInfos(data.surveysInfos);
          self.accountRepository.setTabletName(data.tabletName);
          self.accountRepository.setFoldersInfos(data.foldersInfos);
          return res;
        } else {
          throw 'ERROR.ACCOUNT_UNKNOWN';
        }
      })
      .catch(function (error) {
        switch (error.response?.status) {
          case 401:
            throw 'ERROR.ACCOUNT_BLOCKED';
          case 402:
            throw 'ERROR.TABLET_UNKNOW';
          case 403:
            throw 'ERROR.ACCOUNT_MAX_CLIENT_REACHED';
          // 400 = "licence key incorrecte ou inexistante" renvoyé par le
          // backend. Sans ce case, un 400 tombait dans le `default` et
          // affichait à tort "vérifiez votre connexion internet".
          case 400:
          case 404:
            throw 'ERROR.ACCOUNT_NOT_FOUND';
          case 500:
            throw 'ERROR.SERVER_ERROR';
          default:
            self.setConnected(false);
            throw 'ERROR.ACCOUNT_UNKNOWN';
        }
      });
    return 1;
  }

  async sendDeviceInfos(name = null) {
    // Récupération du niveau de batterie
    const batteryInfo = await Device.getBatteryInfo();

    // Récupération du statut réseau
    const networkStatus = await Network.getStatus();

    // Récupération de la position
    let position = null;
    try {
      position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 5000,
        // Une position de moins d'une minute suffit : évite d'attendre un
        // nouveau fix GPS à chaque synchro.
        maximumAge: 60000,
      });
    } catch (error) {
      console.log('Error getting location', error);
    }

    // Récupération de l'état de l'application
    const appState = await App.getState();

    let deviceData = {
      battery: {
        level: batteryInfo.batteryLevel,
        isCharging: batteryInfo.isCharging,
      },
      network: {
        connected: networkStatus.connected,
        connectionType: networkStatus.connectionType,
      },
      appState: appState.isActive ? 'actif' : 'inactif',
    };

    if (name) {
      deviceData['name'] = name;
    }

    // Ajout de la position si disponible
    if (position) {
      deviceData['location'] = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: position.timestamp,
      };
    }

    let config = {
      headers: {
        'Api-Token': this.accountRepository.getApiKey(),
      },
      ...deviceData,
    };

    var data = await axios
      .post(`${environment.API_HOST}/api/deviceinfos`, config)
      .then((res) => {
        return res.data;
      })
      .catch((error) => {
        console.log('ERROR SEND DEVICE INFOS %o', error);
      });
    return data;
  }

  /**
   * Synchronisation avec le backend.
   * @param allowLicenceSwitch garde-fou interne : après une bascule de
   *   licence, la synchro est relancée une fois sans autoriser une nouvelle
   *   bascule (évite toute boucle).
   */
  async updateAccount(allowLicenceSwitch = true) {
    let config = {
      headers: {
        'Api-Token': this.accountRepository.getApiKey(),
      },
    };
    var data = await axios
      .get(
        `${
          environment.API_HOST
        }/api/accounts?timestamp=${this.accountRepository.getTimeStamp()}`,
        config
      )
      .then((response) =>
        this.applyAccountSync(response.data, allowLicenceSwitch)
      )
      .catch((error) => {
        console.log('ERROR UPDATE ACCOUNT %o', error);
      });
    return data;
  }

  /**
   * Applique une réponse de synchro (GET accounts, ou POST accounts après une
   * bascule de licence) : sondage actif, réglages, infos de compte.
   */
  private async applyAccountSync(data: any, allowLicenceSwitch: boolean) {
    // Vérification de la licence de rattachement AVANT tout le reste :
    // plus de licence -> purge + login ; autre licence -> purge + bascule.
    const licenceState = await this.checkLicenceOnSync(
      data,
      allowLicenceSwitch
    );
    if (licenceState === 'none') {
      return null;
    }
    if (licenceState === 'refetch') {
      return this.updateAccount(false);
    }
    if (licenceState !== 'same') {
      // Bascule effectuée : la réponse du login (même forme que GET accounts)
      // est appliquée directement, sans refaire un appel de synchro.
      return this.applyAccountSync(licenceState, false);
    }

    // Envoi des infos device (batterie, réseau, position) en arrière-plan :
    // la géolocalisation peut prendre jusqu'à 5 s (timeout) et sa réponse
    // n'est pas utilisée ici, elle ne doit pas bloquer la synchro.
    // Après une bascule de licence, login() vient déjà de l'envoyer.
    if (!this.getIsWeb() && allowLicenceSwitch) {
      this.sendDeviceInfos().catch((error) => {
        console.log('ERROR SEND DEVICE INFOS %o', error);
      });
    }

    // Vrai dès qu'un des blocs de priorité ci-dessous a déjà mis à jour le
    // sondage : le bloc final ne doit pas le retélécharger une 2e fois
    // (sondage + toutes ses images étaient récupérés en double à chaque synchro).
    let surveyUpdated = false;

    // Priority 1: activeLicenceSurveyId (determined by backend based on directSelectMode > group > licence)
    if (data.activeLicenceSurveyId) {
      surveyUpdated = true;
      await this.updateSurvey(data.activeLicenceSurveyId)
        .then(() => {
          AppComponent.surveyCanSelectInHand = false;
          this.setConnected(true);
        })
        .catch(() => {
          this.setConnected(false);
        });
    }
    // Priority 2: activeLicenceAgendaId (if agenda mode)
    else if (data.activeLicenceAgendaId) {
      // En mode agenda, la tablette ne peut jamais choisir de sondage en
      // main. On force le flag dès l'entrée, y compris quand aucun
      // événement n'est actif (sinon une tablette venant du mode
      // "onDevice" garderait la valeur périmée true).
      AppComponent.surveyCanSelectInHand = false;

      // Store the agenda ID for future reference
      this.accountRepository.setAgendaId(data.activeLicenceAgendaId);

      // Stocke la date du prochain événement, ou l'efface si le backend
      // n'en renvoie pas : sinon une valeur périmée (ex. horaire modifié
      // côté admin) survivrait à la synchro et resterait affichée.
      if (data.nextAgendaEvent) {
        localStorage.setItem('nextDateEvent', data.nextAgendaEvent);
      } else {
        localStorage.removeItem('nextDateEvent');
      }
      this.reloadFromStorage();

      // Call updateSurvey(null) which will trigger /api/survey endpoint
      // The backend will determine the active survey based on agenda schedule
      surveyUpdated = true;
      await this.updateSurvey(null)
        .then(() => {
          AppComponent.surveyCanSelectInHand = false;
          this.setConnected(true);
        })
        .catch((error) => {
          // If no event is currently active (204 or 404), that's normal
          // Keep the tablet in agenda mode but without an active survey
          if (error === 'ERROR.NO_SURVEY_ACTIVE') {
            this.surveyRepository.setSurvey(null);
            this.setConnected(true);
          } else {
            this.setConnected(false);
          }
        });
    }
    // Priority 3: Legacy group mode (kept for backward compatibility)
    else if (data.group) {
      if (data.group.selectMode.key === 'survey') {
        surveyUpdated = true;
        await this.updateSurvey(data.group.selectMode.survey.surveyId)
          .then(() => {
            AppComponent.surveyCanSelectInHand = false;
            this.setConnected(true);
          })
          .catch(() => {
            this.setConnected(false);
          });
      } else if (data.group.selectMode.key === 'onDevice') {
        AppComponent.surveyCanSelectInHand = true;
        this.setConnected(true);
      } else if (data.group.selectMode.key === 'agenda') {
        // Mode agenda : jamais de sélection en main, même sans événement actif.
        AppComponent.surveyCanSelectInHand = false;
        this.surveyRepository.setSurvey(null);
        surveyUpdated = true;
        await this.updateSurvey(null)
          .then(() => {
            AppComponent.surveyCanSelectInHand = false;
            this.setConnected(true);
          })
          .catch(() => {
            this.setConnected(false);
          });
      }
    }
    // No active survey/agenda - device can select
    else {
      AppComponent.surveyCanSelectInHand = true;
      this.setConnected(true);
    }

    if (data.timestamp) {
      this.accountRepository.setAccountName(data.accountName);
      this.accountRepository.setTabletName(data.tabletName);
      this.accountRepository.setTimeStamp(data.timestamp);
      this.accountRepository.setPassword(data.password);
      this.accountRepository.setSuperPassword(data.superPassword);
      this.accountRepository.setIsInAdminMode(data.isInAdminMode);
      this.accountRepository.setSurveyInfos(data.surveysInfos);
      this.accountRepository.setGroup(data.group);
      this.accountRepository.setLicenceType(data.licenceType);
      this.accountRepository.saveTabletInfos(data.tabletName, null, null, null);
    }
    if (this.setUpdateAppAvailable(data.androidAppVersion)) {
      this.updateDeviceVersion().then(() => {
        console.log('Device version updated');
      });
    }
    if (
      this.accountRepository.getIsInAdminMode() &&
      !this.accountRepository.getGroup()
    ) {
      if (this.hasSurvey()) {
        // Si on passe on mode device, on laisse l'ancien sondage mis pour pas que n'importe qui choisise le sondage mais on laisse la possibilité de changer le sondage dans les params
        var currentSurvey = _.find(
          this.accountRepository.getSurveyInfos(),
          (survey) => {
            return (
              survey.surveyId == this.surveyRepository.getCurrentSurveyId()
            );
          }
        );
        if (currentSurvey) {
          await this.updateSurvey(this.surveyRepository.getCurrentSurveyId())
            .then(() => {
              this.setConnected(true);
            })
            .catch(() => {
              this.setConnected(false);
            });
        } else {
          // the current survey is invalid we need to delete it
          if (this.surveyRepository.getSurvey()) {
            if (this.deleteSurvey(currentSurvey)) {
              this.setConnected(true);
            }
          }
        }
      } else {
        this.setConnected(true);
      }
    } else if (surveyUpdated) {
      // Sondage déjà synchronisé par le bloc de priorités : rien à refaire
      this.setConnected(true);
    } else {
      // Si un survey est sélectionné, passer son ID pour utiliser /api/surveys/:surveyId
      const currentSurveyId = this.hasSurvey()
        ? this.surveyRepository.getCurrentSurveyId()
        : null;
      await this.updateSurvey(currentSurveyId)
        .then(() => {
          this.setConnected(true);
        })
        .catch(() => {
          this.setConnected(false);
        });
    }
    return data;
  }

  /**
   * Compare la licence de rattachement renvoyée par le backend (apps du
   * device, modifiable depuis l'admin) avec celle stockée localement.
   *
   * - 'same'    : même licence (ou web / ancien gateway sans licenceKey) -> rien à faire
   * - 'none'    : la tablette n'est plus rattachée à aucune licence -> données
   *               purgées, redirection vers la page de login
   * - objet    : rattachée à une autre licence -> données purgées, connexion
   *               automatique à la nouvelle licence ; la réponse du login est
   *               renvoyée pour être appliquée comme une synchro
   */
  private async checkLicenceOnSync(
    data: any,
    allowLicenceSwitch: boolean
  ): Promise<'same' | 'none' | 'refetch' | any> {
    if (this.getIsWeb() || data?.licenceKey === undefined) {
      return 'same';
    }

    const backendKey = (data.licenceKey || '').toUpperCase();
    const localKey = (
      this.accountRepository.getClientKey() || ''
    ).toUpperCase();

    if (!backendKey) {
      console.log('SYNC: tablette retirée de sa licence, retour au login');
      await this.purgeLocalData();
      this.router.navigate([''], {
        replaceUrl: true,
        queryParamsHandling: 'merge',
      });
      return 'none';
    }

    if (backendKey === localKey || !allowLicenceSwitch) {
      return 'same';
    }

    console.log(`SYNC: bascule de licence ${localKey} -> ${backendKey}`);
    await this.purgeLocalData();
    let loginData;
    try {
      // Nom vide = le backend conserve le nom actuel de la tablette
      loginData = await this.login(backendKey, '');
    } catch (error) {
      console.log('SYNC: échec de connexion à la nouvelle licence %o', error);
      this.router.navigate([''], {
        replaceUrl: true,
        queryParamsHandling: 'merge',
      });
      return 'none';
    }
    // Réponse inexploitable (cas théorique) : on refera une synchro classique
    return typeof loginData === 'object' && loginData ? loginData : 'refetch';
  }

  /**
   * Purge locale des données de la licence courante (sans appel au backend,
   * contrairement à disconnect() qui détache aussi la tablette côté serveur).
   * Les réponses non envoyées sont d'abord transmises avec la clé courante.
   */
  private async purgeLocalData() {
    if (this.hasNotSentResults()) {
      await this.sendResult().catch((error) => {
        console.log('SYNC: réponses en attente non envoyées %o', error);
      });
    }
    if (this.hasSurvey()) {
      this.deleteSurvey(this.surveyRepository.getSurvey());
    }
    this.accountRepository.deleteAccount();
    this.accountRepository.resetParameters();
    this.answersRepository.clearSentAnswers();
    clearTimeout(this.surveyRepository.getUnusedSurveyTimer());
    clearTimeout(this.surveyRepository.getStartSurveyTimer());
    clearTimeout(this.getCheckUpdateSurveyTimer());
    try {
      let files = await Filesystem.readdir({
        directory: Directory.Data,
        path: ``,
      });
      files.files.forEach((file) => {
        Filesystem.deleteFile({
          directory: Directory.Data,
          path: file.name,
        });
      });
    } catch {
      console.log('Erreur image(s) non supprimée(s)');
    }
  }

  async getVisitorsList() {
    let config = {
      headers: {
        'Api-Token': this.accountRepository.getApiKey(),
      },
    };
    var data = await axios
      .get(
        `${
          environment.API_HOST
        }/api/visitors/${this.surveyRepository.getCurrentSurveyId()}`,
        config
      )
      .then(async (response) => {
        return response.data.visitorsList;
      })
      .catch((error) => {
        console.log('ERROR VISITORS LIST %o', error);
      });
    return data;
  }

  async getHostsByLicenceKey(): Promise<any[]> {
    const licenceKey = this.accountRepository.getClientKey();
    const config = {
      headers: {
        'Api-Token': this.accountRepository.getApiKey(),
      },
    };
    try {
      const response = await axios.get(
        `${environment.API_HOST}/api/hosts/${licenceKey.toUpperCase()}`,
        config
      );
      return response.data?.hosts ?? [];
    } catch (error) {
      console.error('ERROR GET HOSTS %o', error);
      return [];
    }
  }

  async notifyService(serviceName: string, hostId: string): Promise<void> {
    const licenceKey = this.accountRepository.getClientKey();
    const config = {
      headers: {
        'Api-Token': this.accountRepository.getApiKey(),
      },
    };
    try {
      await axios.post(
        `${environment.API_HOST}/api/notify-service`,
        { serviceName, hostId, licenceKey: licenceKey.toUpperCase() },
        config
      );
    } catch (error) {
      console.error('ERROR NOTIFY SERVICE %o', error);
    }
  }

  async exitVisitor(anonymousId: string) {
    const config = {
      headers: {
        'Api-Token': this.accountRepository.getApiKey(),
      },
    };
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const seconds = now.getSeconds().toString().padStart(2, '0');
    const ms = now.getMilliseconds().toString().padStart(3, '0');

    const isoString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}+00:00`;

    const body = {
      exitDate: isoString,
    };

    try {
      const response = await axios.put(
        `${environment.API_HOST}/api/visitors/${anonymousId}`,
        body,
        config
      );
      return response.data;
    } catch (error) {
      console.log('ERROR EXIT VISITOR %o', error);
      throw error;
    }
  }

  hasValidSurvey() {
    var survey = this.surveyRepository.getSurvey();
    if (survey == null) {
      return false;
    }
    if (!survey) return false;
    if (!survey.id) return false;
    if (survey.expirationDate) {
      return Math.floor(Date.now() / 1000) < survey.expirationDate;
    } else return true;
  }

  async sendDatas() {
    await this.sendResult().catch((error) => {
      throw TypeError(error);
    });

    await this.sendInfos().catch((error) => {
      throw TypeError(error);
    });
  }

  /** Dernier envoi de sendInfos (ms) : les montages de page ne renvoient pas
   *  updateDeviceVersion + infos s'ils viennent de partir. */
  private lastSendInfosAt = 0;
  static readonly SEND_INFOS_MIN_INTERVAL_MS = 60 * 1000;

  /**
   * @param force true = envoi systématique (synchro). false = ignoré si un
   *   envoi a eu lieu il y a moins d'une minute (composant de connexion
   *   monté à chaque page : évitait 2 requêtes + une géolocalisation par
   *   navigation).
   */
  async sendInfos(force = true) {
    if (
      !force &&
      Date.now() - this.lastSendInfosAt <
        ExpressyonService.SEND_INFOS_MIN_INTERVAL_MS
    ) {
      return;
    }
    this.lastSendInfosAt = Date.now();
    let config = {
      headers: {
        'Api-Token': this.accountRepository.getApiKey(),
        'App-Version': this.accountRepository.getAppVersion(),
      },
    };
    let infos = { infos: {} };
    infos.infos = this.accountRepository.getDeviceInfos();

    axios
      .post(`${environment.API_HOST}/api/updateDeviceVersion`, null, {
        params: {
          timestamp: Date.now().toPrecision(),
        },
        ...config,
      })
      .then((res) => {
        console.log('Device version updated successfully');
      })
      .catch((error: AxiosError) => {
        console.log(JSON.stringify(error.response));
      });

    axios
      .post(`${environment.API_HOST}/api/infos`, infos, config)
      .then((res) => {
        this.setConnected(true);

        Network.getStatus().then((status) => {
          this.setNetworkStatus(status.connected);
        });

        return res.data;
      })
      .catch((error) => {
        console.log(error);

        if (error.message === 'Network Error') {
          this.setConnected(false);

          Network.getStatus().then((status) => {
            this.setNetworkStatus(status.connected);
          });
        }

        this.accountRepository.setIsActivated(true);
        if (error.response != null && error.response.status == 401) {
          this.accountRepository.setIsActivated(false);
        } else if (
          error.response != null &&
          (error.response.status == 410 || error.response.status == 405)
        ) {
          //la tablette a été supprimée du compte DEPUIS L'ADMINISTRATION
          this.disconnect().then(() => {
            if (error.response.status == 410) {
              this.router.navigate(['/alertUnsubscribed'], {
                replaceUrl: true,
                queryParamsHandling: 'merge',
              });
            }
          });
        }
      });
  }

  async updateSurvey(surveyId = null) {
    surveyId = surveyId === null ? 0 : surveyId;
    var currentSurvey = this.surveyRepository.getSurvey();

    const status = await this.getNetworkStatus();
    if (!this.isConnected || !status) {
      throw 'ERROR.NO_CONNECTION';
    } else if (!this.accountRepository.isLogged()) {
      throw 'ERROR.NOT_LOGGED';
    }

    var handleError = async (value) => {
      console.error('updateSurvey error', value);
      const responseStatus = value?.response?.status;
      //today we have no survey so we need to delete if one exist
      if (responseStatus == 404 || responseStatus == 204) {
        // 404: No survey found (old behavior)
        // 204: No survey currently active in agenda mode
        if (this.hasSurvey()) {
          this.deleteSurvey(currentSurvey);
        }
        // For agenda mode, this is expected when no event is active
        throw 'ERROR.NO_SURVEY_ACTIVE';
      } else if (responseStatus == 405) {
        //la tablette a été supprimée du compte DEPUIS L'ADMINISTRATION
        if (this.hasNotSentResults()) {
          await this.sendResult();
        } //on envoie les données
        this.clearAccount();
        this.router.navigate(['/alertUnsubscribed'], {
          replaceUrl: true,
          queryParamsHandling: 'merge',
        });
      }
      throw 'Error Update survey';
      //TODO handle api error....
    };

    var handleSuccess = async (res) => {
      let data = res?.data;

      // "Pas de sondage actif" (mode onDevice, agenda sans événement en cours) :
      // peut arriver soit en 204 (corps VIDE), soit en 200 avec un body
      // {status:'no surveyId found'}. À tester AVANT le garde `!data`, sinon un
      // 204 (corps vide -> data falsy) est pris à tort pour une erreur serveur.
      if (res?.status === 204 || data?.status === 'no surveyId found') {
        if (this.hasSurvey()) {
          this.deleteSurvey(currentSurvey);
        }
        throw 'ERROR.NO_SURVEY_ACTIVE';
      }

      if (!data) {
        throw 'ERROR.SERVER_ERROR';
      }

      let id = data.id;

      this.surveyRepository.hasUpdateSurvey = false;

      //we have an update first let's check image we already have survey and add the image path on the new survey
      if (this.hasSurvey()) {
        var currentImages = this.getSurveyImageIds(currentSurvey);
        var toDelete = this.copyOldImagesToSurvey(data, currentImages);
        //Download new image and delete old.
        await this.downloadNewImages(data).then((surveyWhitImg) => {
          if (this.deleteImages(toDelete)) {
            this.surveyRepository.setSurvey(surveyWhitImg);
            this.setConnected(true);
          }
        });
      } else {
        await this.downloadNewImages(data).then((surveyWhitImg) => {
          this.surveyRepository.setSurvey(surveyWhitImg);
        });
      }
      return 1;
    };

    let config = {
      headers: {
        'Api-Token': this.accountRepository.getApiKey(),
        'App-Version': this.accountRepository.getAppVersion(),
      },
    };
    if (surveyId == 0) {
      const timestamp = Date.now().toPrecision();
      var data = await axios
        .get(
          `${environment.API_HOST}/api/survey?timestamp=${timestamp}`,
          config
        )
        .then((res) => handleSuccess(res))
        .catch((error) => handleError(error));
    } else {
      var data = await axios
        .get(
          `${environment.API_HOST}/api/surveys/${surveyId}?timestamp=0`,
          config
        )
        .then((res) => handleSuccess(res))
        .catch((error) => handleError(error));
    }
    return data;
  }

  getSurveyImageIds(survey) {
    var image = [];
    if (survey && survey.imageId) {
      image.push({
        id: survey.imageId,
        path: survey.imagePath,
        variant: 'large',
      });
    }
    for (const culture of survey?.cultures ?? []) {
      if (culture.imageFlagId) {
        image.push({
          id: culture.imageFlagId,
          path: culture.imageFlagPath,
          variant: 'small',
        });
      }
    }
    const collectQuestionImages = (question) => {
      if (question.type === 'Category' && question.image !== undefined) {
        image.push({
          id: question.image,
          path: question.imagePath,
          variant: 'large',
        });
      }
      if (
        (question.type === 'QCM' ||
          (question.type === 'Smiley' && question.style === 'custom')) &&
        question.typeInformation !== undefined &&
        question.typeInformation.answers !== undefined &&
        Array.isArray(question.typeInformation.answers)
      ) {
        const variant =
          question.type === 'QCM' && question.typeInformation.qcmType === 2
            ? 'large'
            : 'small';
        question.typeInformation.answers.forEach(function (answer) {
          if (answer.imageId !== undefined) {
            image.push({ id: answer.imageId, path: answer.imagePath, variant });
          }
        });
      }
    };

    if (
      survey &&
      survey.questions !== undefined &&
      Array.isArray(survey.questions)
    ) {
      survey.questions.forEach(collectQuestionImages);
    }
    if (
      survey?.randomQuestions !== undefined &&
      Array.isArray(survey.randomQuestions)
    ) {
      survey.randomQuestions.forEach(collectQuestionImages);
    }
    return image.filter(
      (entry, index, entries) =>
        index ===
        entries.findIndex(
          (candidate) =>
            candidate.id == entry.id && candidate.variant === entry.variant
        )
    );
  }

  copyOldImagesToSurvey(newSurvey, images) {
    var copiedImages = [];
    const findImage = (id, variant) =>
      images.find((image) => image.id == id && image.variant === variant);
    if (newSurvey && newSurvey.imageId) {
      var surveyLogo = findImage(newSurvey.imageId, 'large');
      if (surveyLogo) {
        newSurvey.imagePath = surveyLogo.path;
        copiedImages.push(surveyLogo);
      }
    }
    for (const culture of newSurvey?.cultures ?? []) {
      const flag = findImage(culture.imageFlagId, 'small');
      if (flag) {
        culture.imageFlagPath = flag.path;
        copiedImages.push(flag);
      }
    }
    const copyQuestionImages = (question) => {
      if (question.type === 'Category' && question.image !== undefined) {
        var img = findImage(question.image, 'large');
        if (img) {
          question.imagePath = img.path;
          copiedImages.push(img);
        }
      }
      if (
        (question.type === 'QCM' ||
          (question.type === 'Smiley' && question.style === 'custom')) &&
        question.typeInformation !== undefined &&
        question.typeInformation.answers !== undefined &&
        Array.isArray(question.typeInformation.answers)
      ) {
        const variant =
          question.type === 'QCM' && question.typeInformation.qcmType === 2
            ? 'large'
            : 'small';
        question.typeInformation.answers.forEach(function (answer) {
          var img = findImage(answer.imageId, variant);
          if (img) {
            answer.imagePath = img.path;
            copiedImages.push(img);
          }
        });
      }
    };
    if (Array.isArray(newSurvey?.questions)) {
      newSurvey.questions.forEach(copyQuestionImages);
    }
    if (Array.isArray(newSurvey?.randomQuestions)) {
      newSurvey.randomQuestions.forEach(copyQuestionImages);
    }
    return images.filter((image) => {
      return !copiedImages.find((copiedImage) => {
        return (
          copiedImage.id == image.id && copiedImage.variant === image.variant
        );
      });
    });
  }

  deleteImages(images) {
    //FONCTION A REVOIR
    if (!images || images == null || !images.length) {
      return 1;
    }
    return 1;
  }

  async downloadNewImages(newSurvey) {
    type ImageVariant = 'large' | 'small';
    const localFilePrefix = (imageId, variant: ImageVariant) =>
      `${imageId}.${variant}.`;
    const imageUrl = (imageId, variant: ImageVariant) =>
      `${environment.API_HOST}/v0/expressyon/media/download/${imageId}/${variant}`;

    const resolveImageInfoRaw = async (imageId, variant: ImageVariant) => {
      if (!imageId) return null;
      const url = imageUrl(imageId, variant);

      if (this.getIsWeb()) {
        return {
          imageId,
          imagePath: url,
          imageUrl: url,
          imageWebUrl: url,
        };
      }

      const image = await this.getImage(imageId, variant);
      if (!image) return null;
      const saved = await this.saveImage(image);
      if (!saved) return null;
      return { ...saved, imageUrl: url, imageWebUrl: url };
    };

    const requestedImages = new Map<
      string,
      { imageId: string; variant: ImageVariant; localPath?: string }
    >();
    const consider = (imageId, variant: ImageVariant, localPath?: string) => {
      if (!imageId) return;
      const key = `${imageId}:${variant}`;
      requestedImages.set(key, { imageId, variant, localPath });
    };
    if (newSurvey?.imageId) {
      consider(newSurvey.imageId, 'large', newSurvey.imagePath);
    }
    for (const culture of newSurvey?.cultures ?? []) {
      consider(culture.imageFlagId, 'small', culture.imageFlagPath);
    }
    for (const question of [
      ...(newSurvey?.questions ?? []),
      ...(newSurvey?.randomQuestions ?? []),
    ]) {
      if (
        (question.type === 'QCM' ||
          (question.type === 'Smiley' && question.style === 'custom')) &&
        question.typeInformation?.answers?.length
      ) {
        const variant: ImageVariant =
          question.type === 'QCM' && question.typeInformation.qcmType === 2
            ? 'large'
            : 'small';
        for (const answer of question.typeInformation.answers) {
          consider(answer.imageId, variant, answer.imagePath);
        }
      }
      if (question.type === 'Category' && question.image) {
        consider(question.image, 'large', question.imagePath);
      }
    }

    const resolvedImages = new Map<string, any>();
    await Promise.all(
      [...requestedImages.entries()].map(async ([key, request]) => {
        const expectedLocalPrefix = localFilePrefix(
          request.imageId,
          request.variant
        );
        if (
          !this.getIsWeb() &&
          request.localPath?.startsWith(expectedLocalPrefix) &&
          (await this.localImageExists(request.localPath))
        ) {
          resolvedImages.set(key, {
            imageId: request.imageId,
            imagePath: request.localPath,
            imageUrl: imageUrl(request.imageId, request.variant),
            imageWebUrl: imageUrl(request.imageId, request.variant),
          });
          return;
        }
        resolvedImages.set(
          key,
          await resolveImageInfoRaw(request.imageId, request.variant)
        );
      })
    );
    const resolveImageInfo = (imageId, variant: ImageVariant) =>
      resolvedImages.get(`${imageId}:${variant}`);

    if (newSurvey?.imageId) {
      const data = resolveImageInfo(newSurvey.imageId, 'large');
      if (data) {
        newSurvey.imagePath = data.imagePath;
        newSurvey.imageUrl = data.imageUrl;
        newSurvey.imageWebUrl = data.imageWebUrl;
      }
    }
    for (const culture of newSurvey?.cultures ?? []) {
      const flagInfo = resolveImageInfo(culture.imageFlagId, 'small');
      if (flagInfo) {
        culture.imageFlagPath = flagInfo.imagePath;
        culture.imageFlagUrl = flagInfo.imageUrl;
        culture.imageFlagWebUrl = flagInfo.imageWebUrl;
      }
    }
    const surveyQuestions = [
      ...(newSurvey?.questions ?? []),
      ...(newSurvey?.randomQuestions ?? []),
    ];
    for (const question of surveyQuestions) {
      if (
        (question.type === 'QCM' ||
          (question.type === 'Smiley' && question.style === 'custom')) &&
        question.typeInformation &&
        question.typeInformation.answers &&
        question.typeInformation.answers.length
      ) {
        const variant: ImageVariant =
          question.type === 'QCM' && question.typeInformation.qcmType === 2
            ? 'large'
            : 'small';
        for (const answer of question.typeInformation.answers) {
          if (answer.imageId) {
            const imageInfo = resolveImageInfo(answer.imageId, variant);
            if (imageInfo) {
              answer.imagePath = imageInfo.imagePath;
              answer.imageUrl = imageInfo.imageUrl;
              answer.imageWebUrl = imageInfo.imageWebUrl;
            }
          }
        }
      }
      if (question.type === 'Category' && question.image) {
        const imageInfo = resolveImageInfo(question.image, 'large');
        if (imageInfo) {
          question.imagePath = imageInfo.imagePath;
          question.imageUrl = imageInfo.imageUrl;
          question.imageWebUrl = imageInfo.imageWebUrl;
        }
      }
    }
    return newSurvey;
  }

  private async localImageExists(path?: string) {
    if (!path) return false;
    try {
      await Filesystem.stat({ directory: Directory.Data, path });
      return true;
    } catch {
      return false;
    }
  }

  getImage(imageId, variant: 'large' | 'small') {
    let config = {
      headers: {
        'Api-Token': this.accountRepository.getApiKey(),
      },
    };

    return axios
      .get(
        `${environment.API_HOST}/v0/expressyon/media/download/${imageId}/${variant}`,
        { ...config, responseType: 'arraybuffer' }
      )
      .then((res) => {
        const bytes = new Uint8Array(res.data);
        const chunkSize = 0x8000;
        let binary = '';
        for (let offset = 0; offset < bytes.length; offset += chunkSize) {
          binary += String.fromCharCode(
            ...bytes.subarray(offset, offset + chunkSize)
          );
        }
        return {
          id: imageId,
          variant,
          mimeType: res.headers['content-type'] ?? 'image/jpeg',
          image_content: btoa(binary),
        };
      })
      .catch((error) => {
        console.log('Erreur get image %o', error);
      });
  }

  setImagesPath(survey, imageId, imagePath, imageUrl, imageWebUrl) {
    if (survey && survey.imageId && imageId == survey.imageId) {
      survey.imagePath = imagePath;
      survey.imageUrl = imageUrl;
      survey.imageWebUrl = imageWebUrl;
    }
    const applyImagePathToQuestion = (question) => {
      if (
        question.type === 'Category' &&
        question.image &&
        question.image == imageId
      ) {
        question.imagePath = imagePath;
        question.imageUrl = imageUrl;
        question.imageWebUrl = imageWebUrl;
      }
      if (
        (question.type === 'QCM' ||
          (question.type === 'Smiley' && question.style === 'custom')) &&
        question.typeInformation &&
        question.typeInformation.answers
      ) {
        question.typeInformation.answers.forEach(function (answer) {
          if (answer.imageId && answer.imageId == imageId) {
            answer.imagePath = imagePath;
            answer.imageUrl = imageUrl;
            answer.imageWebUrl = imageWebUrl;
          }
        });
      }
    };

    if (survey?.questions) {
      survey.questions.forEach(applyImagePathToQuestion);
    }
    if (survey?.randomQuestions) {
      survey.randomQuestions.forEach(applyImagePathToQuestion);
    }
    return survey;
  }

  async saveImage(image) {
    if (!image?.id) {
      return null;
    }
    const extension =
      image.mimeType === 'image/png'
        ? 'png'
        : image.mimeType === 'image/webp'
        ? 'webp'
        : image.mimeType === 'image/gif'
        ? 'gif'
        : 'jpg';
    var fileDest = `${image.id}.${image.variant}.${extension}`;

    const savedImage = await Filesystem.writeFile({
      path: fileDest,
      data: image.image_content,
      directory: Directory.Data,
    });
    return {
      imageUrl: savedImage.uri,
      imageId: image.id,
      imagePath: fileDest,
      imageWebUrl: image.url,
    };
  }
  deleteSurvey(survey) {
    var tmp = this.deleteImages(this.getSurveyImageIds(survey));
    this.surveyRepository.deleteSurvey();
    return tmp;
  }

  async getSurveysList() {
    let self = this;
    let surveysList = _.map(
      await self.accountRepository.getSurveyInfos(),
      function (survey) {
        if (survey.surveyId == self.surveyRepository.getCurrentSurveyId()) {
          return _.extend(survey, { isSelected: true });
        } else return _.extend(survey, { isSelected: false });
      }
    );
    return _.uniqBy(surveysList, 'surveyId');
  }

  async getFoldersList() {
    return await this.accountRepository.getFoldersInfos();
  }

  /**
  Vérifie que des mises à jour sont disponibles
  */
  async checkSurvey() {
    const self = this;
    const currentSurvey = this.surveyRepository.getSurvey();
    let config = {
      headers: {
        'Api-Token': this.accountRepository.getApiKey(),
      },
    };
    const currentSurveyId = currentSurvey.id;
    var data = await axios
      .get(
        `${
          environment.API_HOST
        }/api/checksurveys/${currentSurveyId}?timestamp=${encodeURIComponent(
          new Date().toISOString()
        )}`,
        config
      )
      .then(function (response) {
        if (self.setUpdateAppAvailable(response.data.androidAppVersion)) {
          self.updateDeviceVersion().then(() => {
            console.log('Device version updated');
          });
        }
        AppComponent.surveyCanSelectInHand =
          response.data.surveyCanSelectInHand;
        self.surveyRepository.setHasUpdateSurvey(response.data.hasUpdates);
        if (response.data.nextDateEvent) {
          localStorage.setItem('nextDateEvent', response.data.nextDateEvent);
        } else {
          localStorage.removeItem('nextDateEvent');
        }
        self.setConnected(true);
      })
      .catch((error) => {
        if (error.response != null && error.response.status == 401) {
          // le compte a été désactivé
          this.accountRepository.setIsActivated(false);
        } else if (error.data != null && error.data.status == 410) {
          //la tablette a été supprimée du compte DEPUIS L'ADMINISTRATION
          if (this.hasNotSentResults()) {
            this.sendResult();
          } //on envoie les données
        }
        this.setConnected(false);
      });
    return data;
  }

  async updateDeviceVersion() {
    const self = this;
    let config = {
      headers: {
        'Api-Token': this.accountRepository.getApiKey(),
        'App-Version': this.accountRepository.getAppVersion(),
      },
    };
    const timestamp = Date.now();
    return axios
      .post(
        `${environment.API_HOST}/api/updateDeviceVersion?timestamp=${timestamp}`,
        {}, // <-- Ajoute un body vide pour que axios place bien les headers
        config
      )
      .then(function (response) {
        self.setConnected(true);
        return response.data;
      })
      .catch((error) => {
        if (error.response != null && error.response.status == 401) {
          // le compte a été désactivé
          this.accountRepository.setIsActivated(false);
        } else if (error.data != null && error.data.status == 410) {
          //la tablette a été supprimée du compte DEPUIS L'ADMINISTRATION
          if (this.hasNotSentResults()) {
            this.sendResult();
          } //on envoie les données
        }
        this.setConnected(false);
      });
  }

  async sendResult() {
    // Si on est en mode preview on ne doit pas envoyer les résultats
    if (this.getPreview()) {
      this.answersRepository.resultSent();
      return 1;
    }
    // Rien à envoyer : on évite un POST à vide (à chaque synchro, et après
    // une bascule de licence où il partait avec surveyId=0 -> erreur serveur).
    if (!this.hasNotSentResults()) {
      return 1;
    }

    var self = this;
    var currentTimestamp = _.now();
    var form = this.answersRepository.getAnswersObject();
    var key = this.accountRepository.getClientKey();
    var survey = this.surveyRepository.getSurvey();
    var surveyId = this.surveyRepository.getCurrentSurveyId();
    var tabletName = this.accountRepository.getTabletName();
    let IsActivated = self.accountRepository.getIsActivated();
    let isExitMode = this.accountRepository.getIsExitMode();
    let exitVisitorId = this.accountRepository.getExitVisitorId();
    let content = {
      forms: form.forms,
    };
    let config = {
      headers: {
        'Api-Token': this.accountRepository.getApiKey(),
      },
    };

    let infos = `?licenceKey=${key.toUpperCase()}&surveyId=${surveyId}&tabletName=${tabletName}&timestamp=${currentTimestamp}&isExitMode=${isExitMode}`;
    if (exitVisitorId) {
      infos += `&exitVisitorId=${exitVisitorId}`;
    }

    return axios
      .post(`${environment.API_HOST}/api/answers${infos}`, content, config)
      .then(function (res) {
        self.answersRepository.resultSent();
        // Réinitialiser le mode exit et l'ID du visiteur après l'envoi réussi
        self.accountRepository.setIsExitMode(false);
        self.accountRepository.setExitVisitorId(null);
        if (self.getIsWeb() && survey?.uniqueResponse) {
          self.rememberAnonymous();
        } else {
          self.sendDeviceInfos();
        }
        return 1;
      })
      .catch((error) => {
        if (IsActivated && error?.response?.status == 410) {
          //la tablette a été supprimée du compte DEPUIS L'ADMINISTRATION
          self.answersRepository.resultSent();
          self.clearAccount();
          self.router.navigate(['/alertUnsubscribed'], {
            replaceUrl: true,
            queryParamsHandling: 'merge',
          });
        }
        return Promise.reject(error);
      });
  }

  clearAccount() {
    clearTimeout(this.surveyRepository.getUnusedSurveyTimer());
    clearTimeout(this.surveyRepository.getStartSurveyTimer());
    clearTimeout(this.getCheckUpdateSurveyTimer());
    var currentSurvey = this.surveyRepository.getSurvey();
    this.answersRepository.clearSentAnswers();
    this.deleteSurvey(currentSurvey);
    this.accountRepository.deleteAccount();
    this.accountRepository.resetParameters();
  }

  /**
   * Disconnect the current tablet from the account
   */
  async disconnect() {
    let config = {
      headers: {
        'Api-Token': this.accountRepository.getApiKey(),
      },
    };
    var self = this;
    var data = await axios
      .delete(environment.API_HOST + '/api/accounts', config)
      .then(async function (response) {
        if (self.hasSurvey()) {
          var currentSurvey = self.surveyRepository.getSurvey();
          if (self.hasNotSentResults()) {
            self.deleteSurvey(currentSurvey);
            await self.sendResult().then(function () {
              self.accountRepository.deleteAccount();
              self.accountRepository.resetParameters();
              self.answersRepository.clearSentAnswers();
            });
          } else {
            self.deleteSurvey(currentSurvey);
            self.accountRepository.deleteAccount();
            self.accountRepository.resetParameters();
            self.answersRepository.clearSentAnswers();
          }
        } else {
          if (self.hasNotSentResults()) {
            await self.sendResult().then(function () {
              self.accountRepository.deleteAccount();
              self.accountRepository.resetParameters();
              self.answersRepository.clearSentAnswers();
            });
          } else {
            self.accountRepository.deleteAccount();
            self.accountRepository.resetParameters();
          }
        }
      })
      .catch(function (error) {
        if (
          error.response.status == 401 ||
          error.response.status == 400 ||
          error.response.status == 200 ||
          error.response.status == 405 ||
          error.response.status == 410
        ) {
          self.accountRepository.deleteAccount();
        }
      });
    clearTimeout(this.surveyRepository.getUnusedSurveyTimer());
    clearTimeout(this.surveyRepository.getStartSurveyTimer());
    clearTimeout(this.getCheckUpdateSurveyTimer());
    try {
      let files = await Filesystem.readdir({
        directory: Directory.Data,
        path: ``,
      });
      files.files.forEach((file) => {
        Filesystem.deleteFile({
          directory: Directory.Data,
          path: file.name,
        });
      });
    } catch {
      console.log('Erreur image(s) non supprimée(s)');
    }
    return data;
  }

  /**
  /* return if we still hold answers not sent to the server
  /* @returns {boolean}
  /*/
  hasNotSentResults() {
    return this.answersRepository
      .getForms()
      .some(
        (form) => this.answersRepository.countUsersWithAnswers(form.users) > 0
      );
  }

  /**
   * Return list of answers stats for each form
   * @returns {*} [{
                      sent: 44,
                      sentAt: 1406887192630,
                      id: 4,
                      notSent: 12,
                      title: "Titre"
   *          },...]
   */
  getAnswersStats() {
    const self = this;
    return _(self.answersRepository.getForms())
      .union(self.answersRepository.getSentForms())
      .map(function (form) {
        return _(form)
          .extend({
            notSent: self.answersRepository.countUsersWithAnswers(form.users),
          })
          .defaults(
            _.find(self.answersRepository.getSentForms(), { id: form.id })
          )
          .defaults({ sent: 0, sentAt: 0 })
          .omit('users')
          .value();
      })
      .uniq(function (form) {
        return form.id;
      })
      .filter(function (form) {
        return form.sent + form.notSent > 0;
      })
      .value();
  }
  /**
   * return the last result sent timestamp
   * @returns {number} 0 never sent
   */
  getLastSentDate() {
    const self = this;
    return _.some(self.answersRepository.getSentForms(), 'sentAt')
      ? _.max(self.answersRepository.getSentForms(), 'sentAt').sentAt
      : undefined;
  }

  /**
   * clear the sent answer array, if it start to be too big the user can clean it.
   */
  clearSentAnswers() {
    this.answersRepository.clearSentAnswers();
  }

  //Récupére l'image stocké et la return en base 64
  async getImageBase64(imgPath) {
    if (!imgPath || imgPath == '') {
      return 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
    }
    if (imgPath.startsWith('http') || imgPath.startsWith('data:')) {
      return imgPath;
    }
    let img64 =
      'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
    if (this.getIsWeb()) {
      return `${environment.API_HOST}/v0/expressyon/media/download/${imgPath}/large`;
    }
    await Filesystem.readFile({
      directory: Directory.Data,
      path: imgPath,
    })
      .then((result) => {
        const extension = imgPath.split('.').pop()?.toLowerCase();
        const mimeType =
          extension === 'png'
            ? 'image/png'
            : extension === 'webp'
            ? 'image/webp'
            : extension === 'gif'
            ? 'image/gif'
            : 'image/jpeg';
        img64 = `data:${mimeType};base64,${result.data}`;
      })
      .catch((error) => {
        console.log('Error : ', error, imgPath);
      });
    return img64;
  }

  rememberAnonymous() {
    if (!this.anonymousHasAlreadyAnswered()) {
      var responses = this.getWebResponseCookie();
      var survey = this.surveyRepository.getSurvey();
      if (survey != undefined) {
        if (!responses.includes(survey.id.toString())) {
          responses.push(survey.id.toString());
        }
        var expiresDate = new Date();
        expiresDate.setTime(expiresDate.getTime() + 24 * 60 * 60 * 1000);
        document.cookie =
          'ExpressyonWebResponse=' +
          responses.join(',') +
          '; expires=' +
          expiresDate.toUTCString() +
          '; path=/';
      }
    }
  }

  anonymousHasAlreadyAnswered() {
    if (this.surveyRepository.getSurvey() == null) {
      return false;
    }
    var responses = this.getWebResponseCookie();
    for (var i = 0; i < responses.length; i++) {
      if (responses[i] == this.surveyRepository.getSurvey().id.toString()) {
        return true;
      }
    }
    return false;
  }

  getWebResponseCookie() {
    var nameEQ = 'ExpressyonWebResponse=';
    var ca = document.cookie.split(';');
    for (var i = 0; i < ca.length; i++) {
      var c = ca[i];
      while (c.charAt(0) == ' ') {
        c = c.substring(1, c.length);
      }
      if (c.indexOf(nameEQ) == 0) {
        var responses = c.substring(nameEQ.length, c.length);
        return responses.split(',');
      }
    }
    return [];
  }

  private updateIsLaunched = false;

  get IsUpdateLaunched() {
    return this.updateIsLaunched;
  }

  set setUpdateLaunched(launched: boolean) {
    this.updateIsLaunched = launched;
  }

  private agendaDateSubject = new BehaviorSubject<Date | null>(
    this.loadFromStorage()
  );
  agendaDate$ = this.agendaDateSubject.asObservable();

  reloadFromStorage() {
    const date = this.loadFromStorage();
    this.agendaDateSubject.next(date);
  }

  private loadFromStorage(): Date | null {
    const stored = localStorage.getItem('nextDateEvent');
    if (!stored) return null;
    const date = new Date(stored);
    const now = new Date();
    return !isNaN(date.getTime()) && date > now ? date : null;
  }

  restartCheckUpdateTimer(time = 600000) {
    clearTimeout(this.checkUpdateSurveyTimer);

    this.checkUpdateSurveyTimer = setTimeout(() => {
      if (!this.getIsWeb()) {
        this.checkSurvey().then(() => {
          if (
            this.router.url === '/selectSurvey' ||
            this.router.url === '/noSurveyFound' ||
            this.router.url === '/startSurvey' ||
            this.historyNavigationRepository.isFirstQuestion()
          ) {
            // console.log('Check update survey timer triggered');
            if (this.surveyRepository.getHasUpdateSurvey()) {
              this.setUpdateLaunched = true;
              this.router.navigate(['/update'], {
                replaceUrl: true,
                queryParamsHandling: 'merge',
              });
            }
            if (this.router.url === '/noSurveyFound') {
              this.reloadFromStorage();
            }
            if (!this.accountRepository.getIsInAdminMode()) {
              this.restartCheckUpdateTimer(600000);
            }
          } else {
            if (!this.accountRepository.getIsInAdminMode()) {
              this.restartCheckUpdateTimer(60000);
            }
          }
        });
      }
    }, time);
  }
}
